import { useContext, useState } from 'react';
import { nanoid } from '@reduxjs/toolkit';
import { useToast, Link } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MeContext } from '../../../../context/MeContext';
import { MeContextState } from '../../../../store/types/account';
import {
  useLazyGetPassTemplateQuery,
  useCreatePassTemplateMutation,
} from '../../../../store/redux/api/templateApi';
import {
  mapApiResponseToTemplateState,
  mapApiResponseToFormState,
} from '../utils/mapApiResponseToTemplateState';
import { formatTemplatePayload } from '../utils/formatTemplatePayload';
import { useApiErrorMessages } from '../../../../hooks/useApiErrorMessages';
import CustomToast from '../../../../common/CustomToast';
import { TemplateState } from '../../../../store/types/template';
import { FormState } from '../../../../store/types/form';

interface TemplateListItemLike {
  uuid: string;
  templateName?: string;
  [key: string]: unknown;
}

/**
 * Encapsulates the "duplicate template" flow (fetch full template → re-create
 * with a fresh form slug). Copies are always created as drafts, so the
 * published-template limit does not apply until the copy is published.
 */
export const useDuplicateTemplate = (templateData: TemplateListItemLike) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { getBaseErrorMessages } = useApiErrorMessages();
  const [loading, setLoading] = useState(false);

  const editTemplatePath = `/pass-templates/edit/${templateData.uuid}`;

  const { state: meStateRaw } = useContext(MeContext) as {
    state: MeContextState;
  };
  const filterEntity =
    meStateRaw?.entities?.length === 1
      ? meStateRaw.entities[0]
      : (meStateRaw?.filterEntity ?? null);
  const localisationEnabled = meStateRaw?.localisationEnabled ?? false;

  const [fetchTemplate] = useLazyGetPassTemplateQuery();
  const [createPassTemplate] = useCreatePassTemplateMutation();

  const handleDuplicate = async () => {
    if (!templateData?.uuid) return;
    setLoading(true);
    try {
      const response = await fetchTemplate({
        id: templateData.uuid,
        includeDynamicForm: true,
      }).unwrap();

      if (!response?.data) {
        throw new Error('Failed to load template');
      }

      const templateState = mapApiResponseToTemplateState(
        response.data as Parameters<typeof mapApiResponseToTemplateState>[0]
      );
      const formState = mapApiResponseToFormState(response);

      const stateForPayload = {
        ...(templateState as TemplateState),
        dynamicForm: {
          ...(formState as FormState),
          slug: `${(formState as FormState).slug}-${nanoid(8)}`,
        },
      };

      const payload = formatTemplatePayload(
        stateForPayload,
        'create',
        localisationEnabled
      );

      await createPassTemplate({
        entityId: filterEntity?.uuid,
        baseTemplateUuid: templateData.uuid,
        // Copies always start life as drafts.
        passTemplate: { ...payload, status: 'draft' },
      }).unwrap();

      sessionStorage.setItem('passTemplateDuplicateSuccess', 'true');
      if (filterEntity?.uuid) {
        sessionStorage.setItem(
          'passTemplateDuplicateFilterEntityUuid',
          filterEntity.uuid
        );
      }
      window.location.reload();
    } catch (err) {
      console.error(err);

      // The draft cap points at the library, not the template, so its message
      // replaces the "resolve any errors" prompt rather than joining it.
      const baseMessages = getBaseErrorMessages(err);
      if (baseMessages.length > 0) {
        toast({
          render: ({ onClose }) => (
            <CustomToast
              status="error"
              title="Failed to copy template"
              description={
                baseMessages.length === 1 ? baseMessages[0] : baseMessages
              }
              onClose={onClose}
            />
          ),
        });
        return;
      }

      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="error"
            title="Failed to copy template"
            description={
              <>
                Please{' '}
                <Link
                  href={editTemplatePath}
                  color="white"
                  textDecoration="underline"
                  textUnderlineOffset="3px"
                  onClick={(e) => {
                    if (
                      e.button !== 0 ||
                      e.metaKey ||
                      e.ctrlKey ||
                      e.shiftKey ||
                      e.altKey
                    ) {
                      return;
                    }
                    e.preventDefault();
                    onClose();
                    navigate(editTemplatePath);
                  }}
                >
                  resolve any errors
                </Link>{' '}
                in the template and try again.
              </>
            }
            onClose={onClose}
          />
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return { handleDuplicate, loading, editTemplatePath };
};
